<template>
  <PopoverRoot v-model:open="isOpen">
    <slot />
  </PopoverRoot>
</template>

<script setup lang="ts" generic="T extends string = string">
import { PopoverRoot } from "reka-ui";
import { type Ref, computed, ref, toRef } from "vue";

import { useStablePending } from "@/lib/composables/stable-pending";

import {
  type SearchComboboxOption,
  provideSearchComboboxContext,
} from "./context";

const props = withDefaults(
  defineProps<{
    options: readonly SearchComboboxOption<T>[];
    pending?: boolean;
    align?: "start" | "center" | "end";
    matchTriggerWidth?: boolean;
  }>(),
  {
    align: "start",
    matchTriggerWidth: false,
  }
);

const modelValue = defineModel<T | undefined>();
const queryModel = defineModel<string>("query", { default: "" });

const isOpen = ref(false);

const isPending = useStablePending(toRef(props, "pending"));

const select = (next: T | undefined) => {
  modelValue.value = next === ("" as T) ? undefined : next;
  isOpen.value = false;
};

const groupedOptions = computed(() => {
  const ungroupedOptions: SearchComboboxOption[] = [];
  const groups: Record<string, SearchComboboxOption[]> = {};

  for (const option of props.options as readonly SearchComboboxOption[]) {
    const groupName = option.group ?? "";

    if (groupName === "") {
      ungroupedOptions.push(option);
      continue;
    }

    const group = (groups[groupName] ??= []);
    group.push(option);
  }

  return ungroupedOptions.length ? { "": ungroupedOptions, ...groups } : groups;
});

provideSearchComboboxContext({
  isOpen,
  modelValue: modelValue as Ref<string | undefined>,
  options: toRef(() => props.options as readonly SearchComboboxOption[]),
  groupedOptions,
  query: queryModel,
  isPending,
  align: toRef(props, "align"),
  matchTriggerWidth: toRef(props, "matchTriggerWidth"),
  select: select as (id: string | undefined) => void,
});
</script>
